import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Siren, ShieldCheck, Coins, CalendarDays, ChevronLeft, ChevronRight, Loader2, Video, TrendingUp, Smartphone, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, Button } from '../components';
import { getSocket } from '../services/socketService';
import {
  fetchCrisisResponderStatus,
  meldCrisisResponderAan,
  fetchCrisisOvereenkomst,
  vraagCrisisTekencode,
  ondertekenCrisisOvereenkomst,
  fetchCrisisRooster,
  boekCrisisShift,
  annuleerCrisisShift,
  fetchCrisisTokenDashboard,
  fetchCrisisAlarmenVoorResponder,
  accepteerCrisisAlarm,
  dienCrisisUrenIn,
  euroCrisis,
  type CrisisResponderStatus,
  type CrisisRoosterShift,
} from '../services/crisisApi';

type SubTab = 'rooster' | 'tokens' | 'alarmen';

const WEEKDAG = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const SHIFT_KORT: Record<CrisisRoosterShift['shift'], string> = { dag: '☀️ Dag', avond: '🌆 Avond', nacht: '🌙 Nacht' };
const ESCALATIE = ['eigen regio', "buurregio's", 'landelijk'];

// ---------------------------------------------------------------------------
// Onboarding-wizard + crisis-overeenkomst (SMS-2FA)
// ---------------------------------------------------------------------------
const OnboardingWizard: React.FC<{ status: CrisisResponderStatus; onKlaar: () => void }> = ({ status, onKlaar }) => {
  const queryClient = useQueryClient();
  const r = status.responder;
  const s = status.suggestie;
  const [form, setForm] = useState({
    postcode: r?.postcode ?? s.postcode ?? '',
    big_nummer: r?.big_nummer ?? '',
    licentie_nummer: r?.licentie_nummer ?? '',
    professional_type: r?.professional_type ?? s.professional_types[0] ?? '',
    certificaten: (r?.certificaten ?? []).join(', '),
    verzekering_url: r?.verzekering_url ?? s.verzekering_url ?? '',
    verzekering_dekking: String(r?.verzekering_dekking ?? s.verzekering_dekking ?? ''),
    telefoon: r?.telefoon ?? s.telefoon ?? '',
  });
  const [code, setCode] = useState('');
  const [codeVerzonden, setCodeVerzonden] = useState<string | null>(null);

  const { data: overeenkomst } = useQuery({ queryKey: ['crisis', 'overeenkomst'], queryFn: fetchCrisisOvereenkomst, enabled: !!r });

  const aanmelden = useMutation({
    mutationFn: () =>
      meldCrisisResponderAan({
        postcode: form.postcode.trim(),
        big_nummer: form.big_nummer.trim() || undefined,
        licentie_nummer: form.licentie_nummer.trim() || undefined,
        professional_type: form.professional_type || null,
        certificaten: form.certificaten.split(',').map((c) => c.trim()).filter(Boolean),
        verzekering_url: form.verzekering_url || null,
        verzekering_dekking: parseFloat(form.verzekering_dekking),
        telefoon: form.telefoon.trim() || undefined,
      }),
    onSuccess: (d) => {
      queryClient.setQueryData(['crisis', 'responder-status'], d);
      toast.success('Aanmelding opgeslagen — onderteken nu de crisis-overeenkomst');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Aanmelden mislukt'),
  });

  const tekencode = useMutation({
    mutationFn: vraagCrisisTekencode,
    onSuccess: (d) => {
      setCodeVerzonden(d.telefoon_masker);
      toast.success(`SMS-code verzonden naar ${d.telefoon_masker}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Code versturen mislukt'),
  });

  const onderteken = useMutation({
    mutationFn: () => ondertekenCrisisOvereenkomst(code),
    onSuccess: (d) => {
      queryClient.setQueryData(['crisis', 'responder-status'], d);
      toast.success('Crisis-overeenkomst ondertekend — u bent nu crisisresponder');
      onKlaar();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Ondertekenen mislukt'),
  });

  const regio = status.regios.find((x) => x.code === (r?.regio_code ?? s.regio_code));

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-red-50 text-red-600">
            <Siren className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Word crisisresponder in de Landelijke Crisis-Pool</h2>
            <p className="text-sm text-gray-600 mt-1">
              Gegarandeerd maandelijks stand-by inkomen uit de 40%-pool (pro rata naar uw Stand-by Tokens) plus € 180,-/uur bij een uitruk (min. 3 uur, 85% netto via Stripe Connect).
              Eenmalige kwaliteits- en diplomascreening, daarna passwordless ondertekening via SMS.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#0B1F3A]" /> Stap 1 — Toelatingseisen
        </h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <label className="block">
            <span className="text-gray-700">BIG-nummer</span>
            <input value={form.big_nummer} onChange={(e) => setForm({ ...form, big_nummer: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="bijv. 19012345601" />
          </label>
          <label className="block">
            <span className="text-gray-700">Licentie / registratienummer (indien geen BIG)</span>
            <input value={form.licentie_nummer} onChange={(e) => setForm({ ...form, licentie_nummer: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="bijv. NIP / Registerplein" />
          </label>
          <label className="block">
            <span className="text-gray-700">Discipline</span>
            <input value={form.professional_type} onChange={(e) => setForm({ ...form, professional_type: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="bijv. crisis_trauma_specialist" />
          </label>
          <label className="block">
            <span className="text-gray-700">Certificaten (komma-gescheiden)</span>
            <input value={form.certificaten} onChange={(e) => setForm({ ...form, certificaten: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="EMDR, Psychotrauma, BHV" />
          </label>
          <label className="block">
            <span className="text-gray-700">Beroepsaansprakelijkheidsverzekering — dekking (€)</span>
            <input type="number" min={0} value={form.verzekering_dekking} onChange={(e) => setForm({ ...form, verzekering_dekking: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="minimaal 1000000" />
            {parseFloat(form.verzekering_dekking) > 0 && parseFloat(form.verzekering_dekking) < 1_000_000 && <span className="text-xs text-red-600">Minimaal € 1.000.000 dekking vereist.</span>}
          </label>
          <label className="block">
            <span className="text-gray-700">Polisdocument (URL uit uw screening)</span>
            <input value={form.verzekering_url} onChange={(e) => setForm({ ...form, verzekering_url: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
          </label>
          <label className="block">
            <span className="text-gray-700">Postcode (bepaalt uw Home-Regio)</span>
            <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="1234AB" />
            {regio && <span className="text-xs text-gray-500">{regio.naam} — {regio.omschrijving}</span>}
          </label>
          <label className="block">
            <span className="text-gray-700">Mobiel nummer (alarm-SMS en ondertekening)</span>
            <input value={form.telefoon} onChange={(e) => setForm({ ...form, telefoon: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="+31612345678" />
          </label>
        </div>
        <div className="mt-4 flex items-center justify-between">
          {!status.stripe_verified && (
            <span className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Uw Stripe-uitbetaalkoppeling is nog niet geverifieerd; uitbetalingen wachten tot die rond is.
            </span>
          )}
          <Button loading={aanmelden.isPending} onClick={() => aanmelden.mutate()}>
            {r ? 'Gegevens bijwerken' : 'Aanmelding opslaan'}
          </Button>
        </div>
      </Card>

      {r && (
        <Card>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[#0B1F3A]" /> Stap 2 — Crisis-overeenkomst ondertekenen (SMS-2FA)
          </h3>
          <pre className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-xs text-gray-700">{overeenkomst ?? 'Overeenkomst laden…'}</pre>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
            <Button variant="outline" loading={tekencode.isPending} onClick={() => tekencode.mutate()}>
              {codeVerzonden ? 'Nieuwe code sturen' : 'Stuur 6-cijferige SMS-code'}
            </Button>
            {codeVerzonden && (
              <>
                <label className="block">
                  <span className="text-xs text-gray-600">Code (verzonden naar {codeVerzonden})</span>
                  <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" className="mt-1 w-40 rounded-lg border border-gray-300 px-3 py-2 tracking-[0.3em] text-center" placeholder="••••••" />
                </label>
                <Button disabled={code.length !== 6} loading={onderteken.isPending} onClick={() => onderteken.mutate()}>
                  Onderteken
                </Button>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Rooster (dagdelen, vol-is-vol)
// ---------------------------------------------------------------------------
const RoosterTab: React.FC = () => {
  const queryClient = useQueryClient();
  const nu = new Date();
  const [jaar, setJaar] = useState(nu.getFullYear());
  const [maand, setMaand] = useState(nu.getMonth() + 1);

  const { data, isLoading } = useQuery({ queryKey: ['crisis', 'rooster', jaar, maand], queryFn: () => fetchCrisisRooster(jaar, maand) });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['crisis', 'rooster'] });
    queryClient.invalidateQueries({ queryKey: ['crisis', 'tokens'] });
  };
  const boek = useMutation({
    mutationFn: ({ datum, shift }: { datum: string; shift: CrisisRoosterShift['shift'] }) => boekCrisisShift(datum, shift),
    onSuccess: () => {
      toast.success('Shift geclaimd');
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Claimen mislukt'),
  });
  const annuleer = useMutation({
    mutationFn: annuleerCrisisShift,
    onSuccess: () => {
      toast.success('Shift geannuleerd');
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Annuleren mislukt'),
  });

  const vorige = () => {
    if (maand === 1) {
      setMaand(12);
      setJaar(jaar - 1);
    } else setMaand(maand - 1);
  };
  const volgende = () => {
    if (maand === 12) {
      setMaand(1);
      setJaar(jaar + 1);
    } else setMaand(maand + 1);
  };

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#0B1F3A]" /> Stand-by rooster {data ? `· regio ${data.regio_code}` : ''}
        </h3>
        <div className="flex items-center gap-2">
          <button type="button" onClick={vorige} className="p-1.5 rounded hover:bg-gray-100">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium w-36 text-center capitalize">
            {MAANDEN[maand - 1]} {jaar}
          </span>
          <button type="button" onClick={volgende} className="p-1.5 rounded hover:bg-gray-100">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Vaste shifts van 8 uur. Dag 1.0× · Avond 1.5× · Nacht 2.0× · Weekend (vr 18:00 – ma 08:00) 2.5×. Vol is vol: max. 4 overdag, 3 's avonds, 2 's nachts per regio (weekend 4). Annuleren kan tot 24 uur vooraf.
      </p>
      {data && <div className="mt-2 text-sm text-gray-700">Uw tokens deze maand: <strong>{data.mijn_tokens}</strong></div>}

      {isLoading || !data ? (
        <div className="py-10 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left py-1 pr-2 w-24">Datum</th>
                {(['dag', 'avond', 'nacht'] as const).map((s) => (
                  <th key={s} className="text-left py-1 pr-2">
                    {SHIFT_KORT[s]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.dagen.map((d) => {
                const weekend = d.weekdag === 0 || d.weekdag === 6;
                return (
                  <tr key={d.datum} className={weekend ? 'bg-amber-50/40' : ''}>
                    <td className="py-1.5 pr-2 whitespace-nowrap font-medium text-gray-700">
                      {WEEKDAG[d.weekdag]} {d.datum.slice(8)}-{d.datum.slice(5, 7)}
                    </td>
                    {d.shifts.map((sh) => {
                      const mijn = !!sh.mijn_slot_id;
                      const uit = sh.verleden || (sh.vol && !mijn);
                      return (
                        <td key={sh.shift} className="py-1.5 pr-2">
                          <button
                            type="button"
                            disabled={uit || boek.isPending || annuleer.isPending}
                            onClick={() => (mijn ? annuleer.mutate(sh.mijn_slot_id!) : boek.mutate({ datum: d.datum, shift: sh.shift }))}
                            title={mijn ? 'Klik om te annuleren' : sh.vol ? 'Vol is vol' : 'Klik om te claimen'}
                            className={`w-full rounded-md border px-2 py-1 text-left transition-colors ${
                              mijn
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                                : uit
                                  ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'border-gray-200 hover:border-[#0B1F3A] hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex justify-between">
                              <span>{mijn ? '✅ ingeroosterd' : sh.vol ? 'vol' : 'claim'}</span>
                              <span className="font-semibold">{sh.tokens} 🪙</span>
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {sh.geboekt}/{sh.capaciteit} bezet{sh.is_weekend ? ' · weekend 2.5×' : ''}
                            </div>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Tokenwaarde-dashboard
// ---------------------------------------------------------------------------
const TokensTab: React.FC = () => {
  const { data, isLoading } = useQuery({ queryKey: ['crisis', 'tokens'], queryFn: fetchCrisisTokenDashboard });
  if (isLoading || !data) {
    return (
      <div className="py-10 text-center text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }
  const maxWaarde = Math.max(...data.historie.map((h) => h.token_waarde), data.token_waarde_prognose ?? 0, 0.01);
  const bezetting = data.tokens_max_landelijk > 0 ? Math.round((data.tokens_geboekt_landelijk / data.tokens_max_landelijk) * 100) : 0;
  return (
    <div className="space-y-4">
      <Card>
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Coins className="w-4 h-4 text-[#0B1F3A]" /> Wat is 1 token nu waard? — {data.periode}
        </h3>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-lg bg-emerald-50 p-4">
            <div className="text-xs text-emerald-800">Prognose tokenwaarde (huidige bezetting)</div>
            <div className="text-2xl font-bold text-emerald-700">{data.token_waarde_prognose != null ? euroCrisis(data.token_waarde_prognose, 3) : '—'}</div>
            <div className="text-[11px] text-emerald-800">40%-pool {euroCrisis(data.pool_deze_maand)} ÷ {data.tokens_geboekt_landelijk} geboekte tokens</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="text-xs text-gray-600">Bodemwaarde bij 100% bezetting</div>
            <div className="text-2xl font-bold text-gray-800">{data.token_waarde_bij_max_bezetting != null ? euroCrisis(data.token_waarde_bij_max_bezetting, 3) : '—'}</div>
            <div className="text-[11px] text-gray-600">÷ {data.tokens_max_landelijk.toLocaleString('nl-NL')} tokens landelijk max.</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="text-xs text-gray-600">Uw tokens deze maand</div>
            <div className="text-2xl font-bold text-gray-800">{data.mijn_tokens}</div>
            <div className="text-[11px] text-gray-600">prognose uitbetaling {euroCrisis(data.mijn_prognose)}</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="text-xs text-gray-600">Landelijke bezetting</div>
            <div className="text-2xl font-bold text-gray-800">{bezetting}%</div>
            <div className="text-[11px] text-gray-600">
              {data.actieve_abonnementen} abonnementen · inleg {euroCrisis(data.inleg_deze_maand)} · regio {data.regio_code}: {data.tokens_geboekt_regio}/{data.tokens_max_regio}
            </div>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Op de 1e van de volgende maand om 00:05 berekent de cron-job de definitieve waarde: 40% van alle abonnementsgelden van de maand gedeeld door alle verdiende tokens. Hoe minder collega's inroosteren, hoe hoger de waarde per token — de prognose schuift daarom tot het einde van de maand.
        </p>
      </Card>

      <Card>
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#0B1F3A]" /> Ontwikkeling per maand
        </h3>
        {data.historie.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">Nog geen afgeronde maand. De eerste payout-run verschijnt hier op de 1e van de volgende maand.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {data.historie.map((h) => (
              <div key={h.periode} className="flex items-center gap-3 text-xs">
                <span className="w-16 text-gray-600">{h.periode}</span>
                <div className="flex-1 h-5 rounded bg-gray-100 overflow-hidden">
                  <div className="h-full bg-[#0B1F3A]/80" style={{ width: `${Math.max((h.token_waarde / maxWaarde) * 100, 2)}%` }} />
                </div>
                <span className="w-20 text-right font-semibold">{euroCrisis(h.token_waarde, 3)}</span>
                <span className="w-44 text-gray-500">
                  pool {euroCrisis(h.pool_totaal, 0)} · {h.totaal_tokens} tokens · {h.aantal_responders} zzp
                </span>
                <span className="w-32 text-right">{h.mijn_tokens > 0 ? `u: ${h.mijn_tokens} 🪙 → ${euroCrisis(h.mijn_bedrag)}` : '—'}</span>
              </div>
            ))}
            {data.token_waarde_prognose != null && (
              <div className="flex items-center gap-3 text-xs">
                <span className="w-16 text-gray-600">{data.periode}*</span>
                <div className="flex-1 h-5 rounded bg-gray-100 overflow-hidden">
                  <div className="h-full bg-emerald-500/70" style={{ width: `${Math.max((data.token_waarde_prognose / maxWaarde) * 100, 2)}%` }} />
                </div>
                <span className="w-20 text-right font-semibold text-emerald-700">{euroCrisis(data.token_waarde_prognose, 3)}</span>
                <span className="w-44 text-gray-500">* prognose lopende maand</span>
                <span className="w-32" />
              </div>
            )}
          </div>
        )}
      </Card>

      <Card>
        <h3 className="font-semibold text-gray-900">Token-staffel</h3>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {data.staffel.map((s) => (
            <div key={s.shift} className="rounded-lg border border-gray-200 p-3">
              <div className="font-medium">{s.label}</div>
              <div className="text-gray-600">
                factor {s.factor} · {s.tokens_per_shift} tokens/shift
              </div>
            </div>
          ))}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="font-medium">Weekend vr 18:00 – ma 08:00</div>
            <div className="text-gray-600">factor {data.weekend_factor} · 20 tokens/shift</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Alarmen (accepteren, videolink, uren indienen)
// ---------------------------------------------------------------------------
const AlarmenTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['crisis', 'alarmen'], queryFn: fetchCrisisAlarmenVoorResponder, refetchInterval: 10000 });
  const [uren, setUren] = useState<Record<string, string>>({});
  const [reis, setReis] = useState<Record<string, string>>({});
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['crisis', 'alarmen'] });

  const accepteer = useMutation({
    mutationFn: accepteerCrisisAlarm,
    onSuccess: (r) => {
      if (r.duo_partner) {
        toast.success(`Duo-match compleet! Uw partner: ${r.duo_partner.naam}${r.duo_partner.telefoon ? ` (${r.duo_partner.telefoon})` : ''} — duo-chat geopend`, { duration: 12000 });
      } else if (r.aantal_geaccepteerd < r.aantal_gewenst) {
        toast.success(`Geaccepteerd als professional #${r.volgnummer} (${r.aantal_geaccepteerd}/${r.aantal_gewenst}) — wacht op de tweede specialist`, { duration: 8000 });
      } else {
        toast.success(`Geaccepteerd (${r.aantal_geaccepteerd}/${r.aantal_gewenst}) — videolink staat klaar`);
      }
      if (r.gedekt_door === 'FACTUUR') {
        toast(`Let op: uw uitruk (#${r.volgnummer}) valt buiten het tegoed van de opdrachtgever en wordt via de verzamelfactuur uitbetaald.`, { duration: 10000, icon: '🧾' });
      }
      window.open(r.video_url, '_blank', 'noopener');
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Accepteren mislukt'),
  });
  const dienIn = useMutation({
    mutationFn: ({ id, u, r }: { id: string; u: number; r: number }) => dienCrisisUrenIn(id, u, r),
    onSuccess: () => {
      toast.success('Uren automatisch goedgekeurd');
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Indienen mislukt'),
  });

  if (isLoading || !data) {
    return (
      <div className="py-10 text-center text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }
  if (data.length === 0) {
    return <Card className="text-sm text-gray-600">Geen openstaande alarmen. Bij een alarm in uw regio krijgt u een push-notificatie en SMS; u heeft 60 seconden voordat de buurregio's worden ingeschakeld.</Card>;
  }
  return (
    <div className="space-y-3">
      {data.map((a) => {
        const mijn = a.mijn_response;
        const kanAccepteren = a.status === 'OPEN' && !mijn;
        return (
          <Card key={a.id} className={a.status === 'OPEN' && !mijn ? 'border-red-300' : ''}>
            <div className="flex flex-col md:flex-row md:items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Siren className={`w-4 h-4 ${a.status === 'OPEN' ? 'text-red-600 animate-pulse' : 'text-gray-400'}`} />
                  <span className="font-semibold text-gray-900">
                    {a.response_type === 'DUAL' ? '👥 Dual-Response' : '👤 Single-Response'} · {a.bedrijfsnaam ?? 'Opdrachtgever'} {a.vestiging_naam ? `· ${a.vestiging_naam}` : ''}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                    regio {a.regio_code} · {ESCALATIE[a.escalatie_niveau] ?? 'landelijk'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {a.aantal_geaccepteerd}/{a.aantal_gewenst} geaccepteerd · {new Date(a.created_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                {a.omschrijving && <p className="text-sm text-gray-700 mt-1">{a.omschrijving}</p>}
                {mijn && (
                  <div className="mt-2 text-xs text-gray-600">
                    {a.vestiging_adres && <div>📍 {a.vestiging_adres}, {a.vestiging_plaats}</div>}
                    {a.video_url && (
                      <a href={a.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B1F3A] underline">
                        <Video className="w-3.5 h-3.5" /> BeLagom-videolink voor directe eerste opvang
                      </a>
                    )}
                    <div className="mt-1">
                      {mijn.gedekt_door === 'FONDS'
                        ? '🛡️ Uw uitruk is gedekt door het Garantiefonds — uitbetaling direct na urenregistratie'
                        : '🧾 Uw uitruk valt buiten het tegoed — uitbetaling na betaling van de verzamelfactuur'}
                      {a.response_type === 'DUAL' && mijn.volgnummer ? ` (professional #${mijn.volgnummer} van ${a.aantal_gewenst})` : ''}
                    </div>
                    {a.response_type === 'DUAL' && (
                      <div className="mt-2 rounded-lg border border-[#0B1F3A]/20 bg-blue-50 p-2 text-blue-900">
                        {a.duo_partner ? (
                          <>
                            <div className="font-medium">👥 Duo-partner: {a.duo_partner.naam}{a.duo_partner.telefoon ? ` · 📞 ${a.duo_partner.telefoon}` : ''}</div>
                            {a.chat_conversation_id && (
                              <a href={`/professionals/chat?conversation=${a.chat_conversation_id}`} className="mt-1 inline-flex items-center gap-1 rounded-md bg-[#0B1F3A] px-2 py-1 text-[11px] font-medium text-white">
                                💬 Open duo-chat
                              </a>
                            )}
                          </>
                        ) : (
                          <div>⏳ Wachten op de tweede specialist (First-Two-Accept). Zodra die accepteert, ontvangt u naam, nummer en een duo-chat.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="md:w-72">
                {kanAccepteren && (
                  <Button variant="danger" className="w-full" loading={accepteer.isPending} onClick={() => accepteer.mutate(a.id)}>
                    🚨 Accepteren & uitrukken
                  </Button>
                )}
                {mijn && mijn.uren == null && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input type="number" step="0.25" min="0.25" max="24" placeholder="uren" value={uren[a.id] ?? ''} onChange={(e) => setUren({ ...uren, [a.id]: e.target.value })} className="w-1/2 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                      <input type="number" step="0.01" min="0" placeholder="reiskosten €" value={reis[a.id] ?? ''} onChange={(e) => setReis({ ...reis, [a.id]: e.target.value })} className="w-1/2 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <Button className="w-full" size="sm" disabled={!(parseFloat(uren[a.id] ?? '') > 0)} loading={dienIn.isPending} onClick={() => dienIn.mutate({ id: a.id, u: parseFloat(uren[a.id]), r: parseFloat(reis[a.id] || '0') })}>
                      Uren indienen (min. 3 u à € 180)
                    </Button>
                  </div>
                )}
                {mijn && mijn.uren != null && (
                  <div className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-900">
                    <div className="flex items-center gap-1 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {mijn.uren_gefactureerd} u goedgekeurd
                    </div>
                    <div>Netto {euroCrisis(mijn.zzp_netto)} · {mijn.gedekt_door === 'FONDS' ? 'Garantiefonds' : 'Verzamelfactuur'} · {mijn.uitbetaling_status}</div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------
export const ProfessionalCrisisPool: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<SubTab>('rooster');
  const { data: status, isLoading } = useQuery({ queryKey: ['crisis', 'responder-status'], queryFn: fetchCrisisResponderStatus });

  // Live alarm via Socket.IO → alarmen-tab openen en lijst verversen
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const handler = () => {
      toast('🚨 CRISIS-ALARM in uw regio — bekijk het tabblad Alarmen', { duration: 15000 });
      setTab('alarmen');
      queryClient.invalidateQueries({ queryKey: ['crisis', 'alarmen'] });
    };
    const matchHandler = (p: { partner?: string | null }) => {
      toast.success(`👥 Duo-match compleet${p?.partner ? ` met ${p.partner}` : ''} — open het tabblad Alarmen voor de duo-chat`, { duration: 12000 });
      setTab('alarmen');
      queryClient.invalidateQueries({ queryKey: ['crisis', 'alarmen'] });
    };
    socket.on('crisis_alarm', handler);
    socket.on('crisis_alarm_matched', matchHandler);
    return () => {
      socket.off('crisis_alarm', handler);
      socket.off('crisis_alarm_matched', matchHandler);
    };
  }, [queryClient]);

  const tabs = useMemo(
    () => [
      { key: 'rooster' as SubTab, label: 'Stand-by rooster', icon: CalendarDays },
      { key: 'tokens' as SubTab, label: 'Tokenwaarde', icon: Coins },
      { key: 'alarmen' as SubTab, label: 'Alarmen', icon: Siren },
    ],
    []
  );

  if (isLoading || !status) {
    return (
      <div className="py-10 text-center text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }

  if (!status.responder?.is_crisis_responder || status.responder.status !== 'ACTIEF') {
    return <OnboardingWizard status={status} onKlaar={() => setTab('rooster')} />;
  }

  const regio = status.regios.find((r) => r.code === status.responder!.regio_code);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Siren className="w-5 h-5 text-red-600" /> Crisisdienst — {regio?.naam ?? status.responder.regio_code}
          </h2>
          <p className="text-xs text-gray-500">
            Geaccrediteerd crisisresponder sinds {status.responder.overeenkomst_getekend_op ? new Date(status.responder.overeenkomst_getekend_op).toLocaleDateString('nl-NL') : '—'}
            {!status.stripe_verified && ' · ⚠️ Stripe-koppeling nog niet geverifieerd (uitbetalingen wachten)'}
          </p>
        </div>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm ${tab === t.key ? 'bg-white shadow text-[#0B1F3A] font-medium' : 'text-gray-600'}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      {tab === 'rooster' && <RoosterTab />}
      {tab === 'tokens' && <TokensTab />}
      {tab === 'alarmen' && <AlarmenTab />}
    </div>
  );
};

export default ProfessionalCrisisPool;
